import "./cbb-new-onboarding.css";
import "@/pages/cbb-builder/AiBot.css";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { axiosGet, axiosPost } from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import { processUrl } from "@/utils/processUrl";
import environment from "@/constants/Environment";
import { getCookie, setCookie, deleteCookies } from "@/services/cookieService";
import FetchUrlModal from "@/pages/cbb-builder/components/FetchUrlModal";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";
import { BotContext } from "@/pages/cbb-builder/store";
import { buildChatConfigFromBranding } from "@/utils/brandingChatConfig";
import type { State } from "@/redux/Types";
import type { Url } from "@/pages/cbb-builder/types";
import { INITIAL_ONBOARDING_VALUES } from "./constants";
import OnboardingFetchOverlay from "./components/OnboardingFetchOverlay";
import OnBoardingPreview from "./components/OnBoardingPreview";
import OnBoardingStepsWrapper from "./components/OnBoardingStepsWrapper";
import OnboardingProgressToast from "./components/OnboardingProgressToast";
import { useDocumentStatusPolling } from "@/pages/agent/hooks/useDocumentStatusPolling";
import useBotSave from "./../../../src/pages/cbb-builder/react-query/useBotSave";
import { steps } from "./onBoardingSteps";
import type {
  OnboardingStepId,
  OnboardingValues,
  AppearancePreviewTab,
  StepAction,
  StepFieldName,
} from "./types";
import SmallScreenModal from "../omni-builder/components/SmallScreenModal";
import { ChatPreviewIcon } from "@/pages/cbb-builder/components/AiBotAssets";
import { ArrowIcon } from "../templates/Templates";
import { getViewportAccessState } from "@/utils/viewportAccessGate";

const environmentData = environment();
const ONBOARDING_PREVIEW_BREAKPOINT = 1280;
const ONBOARDING_PAGE_STORAGE_VERSION = "v2";
const FAILED_TRAINING_STATUSES = new Set([
  "Link Rejected",
  "Limit Reached",
  "Re-sync Failed",
]);
export const OnBoardingWrapper = ({
  inline = false,
  onBotCreated,
  onComplete,
  onSkip,
}: {
  inline?: boolean;
  onBotCreated?: (botUrl: string) => void;
  onComplete?: () => void;
  onSkip?: () => void;
} = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { botUrl } = useParams();
  const botCreatedCalledRef = useRef(false);
  const isSmallScreenRef = useRef<boolean | null>(null);
  const [store, storeDispatch] = useContext(BotContext);
  const { mutateAsync: saveBot, status: updateStatus } = useBotSave();
  const plan = useSelector((state: State) => state.user.plan);
  const userId = useSelector((state: State) => state.user.userId);
  const isNewUser = useSelector((state: State) => state.user.isNewUser);
  const companyDetails = useSelector(
    (state: State) => state.user.companyDetails,
  );
  const [activeStep, setActiveStep] = useState<OnboardingStepId>(1);
  const [appearancePreviewTab, setAppearancePreviewTab] =
    useState<AppearancePreviewTab>("welcome");
  const previousPreviewStepRef = useRef<OnboardingStepId | null>(null);
  const [completedStepIds, setCompletedStepIds] = useState<OnboardingStepId[]>(
    [],
  );
  const [values, setValues] = useState<OnboardingValues>(
    INITIAL_ONBOARDING_VALUES,
  );
  const [fetchModal, setFetchModal] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [fetchMoreLoader, setFetchMoreLoader] = useState(false);
  const [extraLoader, setExtraLoader] = useState(false);
  const [botCreateLoader, setBotCreateLoader] = useState(false);
  const [fetchRemains, setFetchRemains] = useState(false);
  const [allTrained, setAllTrained] = useState(false);
  const [extraUrl, setExtraUrl] = useState("");
  const [fetchedRootUrl, setFetchedRootUrl] = useState("");
  const [overlayDomain, setOverlayDomain] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [isFinishingSetup, setIsFinishingSetup] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLandscapeModeModal, setShowLandscapeModeModal] = useState(false);
  const [showSmallScreenModal, setShowSmallScreenModal] = useState(false);
  const hasHydratedPageStateRef = useRef(false);
  const hasRefreshedBotDataRef = useRef(false);
  const onboardingPageStorageKey = useMemo(() => {
    if (inline || !userId) return null;

    return `cbb-onboarding-page:${ONBOARDING_PAGE_STORAGE_VERSION}:${userId}:standard`;
  }, [inline, userId]);

  useEffect(() => {
    const updateScreenSize = () => {
      const small = window.innerWidth <= ONBOARDING_PREVIEW_BREAKPOINT;
      setIsSmallScreen(small);
      const viewportAccess = getViewportAccessState();

      setShowLandscapeModeModal(viewportAccess.shouldShowLandscapeModal);
      setShowSmallScreenModal(viewportAccess.shouldShowSmallScreenModal);

      if (small !== isSmallScreenRef.current) {
        if (small) {
          setShowPreview(false);
        }
        isSmallScreenRef.current = small;
      }
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);

    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  const currentStep = useMemo(() => {
    const resolvedStep =
      steps.find((step) => step.id === activeStep) ?? steps[0];

    if (!resolvedStep) {
      return steps[0];
    }

    return resolvedStep;
  }, [activeStep]);
  const aiSourceRef = useRef(store.botData?.aiSource ?? {});

  useEffect(() => {
    aiSourceRef.current = store.botData?.aiSource ?? {};
  }, [store.botData?.aiSource]);

  useEffect(() => {
    if (!onboardingPageStorageKey || hasHydratedPageStateRef.current) return;

    hasHydratedPageStateRef.current = true;

    try {
      const rawState = window.sessionStorage.getItem(onboardingPageStorageKey);
      if (!rawState) return;

      const persistedState = JSON.parse(rawState);
      const validStepIds = new Set(steps.map((step) => step.id));
      const persistedStep = Number(persistedState?.activeStep);

      if (validStepIds.has(persistedStep as OnboardingStepId)) {
        setActiveStep(persistedStep as OnboardingStepId);
      }

      if (
        persistedState?.appearancePreviewTab === "welcome" ||
        persistedState?.appearancePreviewTab === "conversation"
      ) {
        setAppearancePreviewTab(persistedState.appearancePreviewTab);
      }

      if (Array.isArray(persistedState?.completedStepIds)) {
        setCompletedStepIds(
          persistedState.completedStepIds.filter((stepId: number) =>
            validStepIds.has(stepId as OnboardingStepId),
          ),
        );
      }

      if (persistedState?.values) {
        setValues((currentValues) => ({
          ...currentValues,
          ...persistedState.values,
        }));
      }

      if (persistedState?.botData?.botCustomId && !store.botData?.botCustomId) {
        storeDispatch({
          type: "set-botData",
          botData: persistedState.botData,
        });
      }

      if (
        persistedState?.onboardingBotData?.botdata?.botCustomId &&
        !store.onboardingBotData?.botdata?.botCustomId
      ) {
        storeDispatch({
          type: "set-onboarding-botData",
          onboardingBotData: persistedState.onboardingBotData,
        });
      }
    } catch {
      window.sessionStorage.removeItem(onboardingPageStorageKey);
    }
  }, [
    onboardingPageStorageKey,
    store.botData?.botCustomId,
    store.onboardingBotData?.botdata?.botCustomId,
    storeDispatch,
  ]);

  useEffect(() => {
    if (previousPreviewStepRef.current === activeStep) return;

    previousPreviewStepRef.current = activeStep;
    if (activeStep === 2 || activeStep === 4) {
      setAppearancePreviewTab("conversation");
    }
  }, [activeStep]);

  useEffect(() => {
    if (!onboardingPageStorageKey || !hasHydratedPageStateRef.current) return;

    window.sessionStorage.setItem(
      onboardingPageStorageKey,
      JSON.stringify({
        activeStep,
        appearancePreviewTab,
        completedStepIds,
        values,
        botData: store.botData?.botCustomId ? store.botData : null,
        onboardingBotData: store.onboardingBotData?.botdata?.botCustomId
          ? store.onboardingBotData
          : null,
      }),
    );
  }, [
    activeStep,
    appearancePreviewTab,
    completedStepIds,
    onboardingPageStorageKey,
    store.botData,
    store.onboardingBotData,
    values,
  ]);

  useEffect(() => {
    if (inline || !userId) return;

    const hasOnboardingAccess = Boolean(getCookie(`onBoarding:${userId}`));

    if (!hasOnboardingAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [inline, navigate, userId]);

  useEffect(() => {
    const seededAgentName = store.botData?.projectName?.trim() || "";

    if (!seededAgentName) return;

    setValues((currentValues) => {
      if (currentValues.agentName.trim()) return currentValues;

      return {
        ...currentValues,
        agentName: seededAgentName,
      };
    });
  }, [store.botData?.projectName]);

  const syncPollingGroup = useCallback(
    (sourceKey: string, updatedItems: any[] = []) => {
      if (!updatedItems.length) return;

      const currentItems = aiSourceRef.current?.[sourceKey] || [];
      if (!Array.isArray(currentItems) || currentItems.length === 0) return;

      let hasChanged = false;

      const updatedAiSourceItems = currentItems.map((currentItem: any) => {
        const matchingApiItem = updatedItems.find((apiItem: any) => {
          const hasMatchingId =
            String(apiItem?.id ?? "") === String(currentItem?.id ?? "");
          const hasMatchingTitle =
            !!apiItem?.title &&
            !!currentItem?.title &&
            apiItem.title === currentItem.title;

          if (sourceKey === "googlepicker") {
            // googlePickerId is the only per-row identifier — `id` is the
            // shared Google config id. When both sides have one it decides the
            // match outright; title is the fallback for older rows.
            if (currentItem?.googlePickerId) {
              const apiPickerId = String(
                apiItem?.googlePickerId ?? apiItem?.id ?? "",
              );
              if (apiPickerId === String(currentItem.googlePickerId)) {
                return true;
              }
              if (apiItem?.googlePickerId) return false;
            }

            // Never fall back to `id` here: it is the same Google config id on
            // every row, so one polled entry would match (and overwrite) all of
            // them — flipping finished files back to "In Progress".
            return hasMatchingTitle;
          }

          return hasMatchingId || hasMatchingTitle;
        });

        if (!matchingApiItem) {
          return currentItem;
        }

        const nextItem = {
          ...currentItem,
          status: matchingApiItem.status,
          lastSyncTime: matchingApiItem.lastSyncTime,
          characterCount:
            matchingApiItem.characterCount ?? currentItem.characterCount ?? 0,
          title: currentItem.title,
          mime: currentItem.mime,
          size: currentItem.size,
        };

        if (
          nextItem.status !== currentItem.status ||
          nextItem.characterCount !== currentItem.characterCount
        ) {
          hasChanged = true;
        }

        return nextItem;
      });

      // Skip the dispatch when nothing actually moved. Re-dispatching an
      // identical array replaces aiSource by identity on every tick, which
      // retriggers the polling effects and keeps the row spinning forever.
      if (!hasChanged) return;

      // Keep the ref in step immediately. The effect that refreshes it runs
      // after the next render, but the next poll can fire first and would
      // otherwise map over this same stale snapshot again.
      aiSourceRef.current = {
        ...aiSourceRef.current,
        [sourceKey]: updatedAiSourceItems,
      };

      storeDispatch({
        type: "set-aiSource",
        dataKey: sourceKey,
        dataSet: updatedAiSourceItems,
      });
    },
    [storeDispatch],
  );

  const { restartPolling } = useDocumentStatusPolling(
    store.botData?.botCustomId,
    store.botData,
    {
      enabled: true,
      interval: 15000,
      maxRetries: 3,
      onStatusChange: (statusData) => {
        syncPollingGroup("documents", statusData.documents ?? []);
        syncPollingGroup("googlepicker", statusData.googlepicker ?? []);
        syncPollingGroup("notion", statusData.notion ?? []);
        syncPollingGroup("confluence", statusData.confluence ?? []);
        syncPollingGroup("salesforce", statusData.salesforce ?? []);

        // Once nothing is pending, pull the authoritative bot record so the
        // character count and any server-side fields reconcile — the same
        // step the builder page already does on completion.
        const polled = [
          ...(statusData.documents ?? []),
          ...(statusData.googlepicker ?? []),
          ...(statusData.notion ?? []),
          ...(statusData.confluence ?? []),
          ...(statusData.salesforce ?? []),
        ];

        const stillPending = polled.some((doc: any) =>
          ["In Progress", "Processing", "Re-syncing"].includes(doc?.status),
        );

        if (polled.length > 0 && !stillPending && botUrl) {
          queryClient.refetchQueries({
            queryKey: ["bot", "completeBot", { botUrl }],
            exact: true,
          });
        }
      },
      onError: (error) => {
        console.error("Documents polling error:", error);
      },
    },
  );

  const currentStepIndex = steps.findIndex((step) => step.id === activeStep);
  const currentField = currentStep.field;
  const isCurrentFieldInvalid = Boolean(
    currentField?.required && !values[currentField.name].trim(),
  );
  const hasTrainingContent = useMemo(() => {
    const aiSource = store.botData?.aiSource ?? {};

    return Object.entries(aiSource).some(([sourceType, source]) => {
      if (sourceType === "textArea") {
        const hasPersistedText = Boolean(
          (source as Record<string, any> | undefined)?.contentId,
        );
        const trainedText = store.textAreaContentPrev?.trim() ?? "";
        const currentText = store.textAreaContent?.trim() ?? "";

        return (
          hasPersistedText &&
          Boolean(trainedText) &&
          trainedText === currentText
        );
      }

      return (
        Array.isArray(source) &&
        source.some((item) => !FAILED_TRAINING_STATUSES.has(item?.status ?? ""))
      );
    });
  }, [
    store.botData?.aiSource,
    store.textAreaContent,
    store.textAreaContentPrev,
  ]);
  const [hasUnlockedAgentPreview, setHasUnlockedAgentPreview] =
    useState(hasTrainingContent);

  useEffect(() => {
    if (hasTrainingContent) {
      setHasUnlockedAgentPreview(true);
    }
  }, [hasTrainingContent]);

  const goToStepByOffset = (offset: number) => {
    const nextStepIndex = currentStepIndex + offset;
    const nextStep = steps[nextStepIndex];

    if (nextStep) {
      setFieldError("");
      setActiveStep(nextStep.id);
      setCompletedStepIds(steps.slice(0, nextStepIndex).map((step) => step.id));
    }
  };

  const handleFieldChange = (fieldName: StepFieldName, value: string) => {
    setFieldError("");
    setValues((currentValues) => ({
      ...currentValues,
      [fieldName]: value,
    }));
  };

  const showError = useCallback(
    (message: string) => {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message,
        }),
      );
    },
    [dispatch],
  );

  const sanitizeUrl = useCallback((url: string) => {
    if (!url) throw new Error("Empty url");
    if (!isNaN(Number(url))) throw new Error("Invalid url");

    const urlWithProtocol = url.startsWith("http") ? url : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);

    if (!["http:", "https:"].includes(urlObj.protocol)) {
      throw new Error("Invalid url");
    }
    if (!urlObj.host.includes(".")) throw new Error("Invalid url");

    return urlObj.origin + urlObj.pathname + urlObj.search;
  }, []);

  const brandingUrl = useCallback((url: string) => {
    const { origin } = new URL(url);
    return origin;
  }, []);

  const normalizePersistedBotData = useCallback(
    (botData: Record<string, any>): Record<string, any> => {
      return {
        ...botData,
        isDraft: false,
        projectName: botData.projectName ?? "",
        aiSource: {
          urls: [],
          youtube: [],
          documents: [],
          ...(botData.aiSource ?? {}),
        },
        dataSources: {
          urls: [],
          youtube: [],
          documents: [],
          ...(botData.dataSources ?? {}),
        },
        aiBotConfig: {
          ...(botData.aiBotConfig ?? {}),
        },
      };
    },
    [],
  );

  // On refresh the bot is restored from sessionStorage, which is a snapshot from
  // whenever it was last written — it misses anything the server resolved since
  // (training statuses, aiBotConfig.configId). Re-fetch completeBot once so the
  // store matches the server rather than a stale copy.
  useEffect(() => {
    const persistedUrl = store.botData?.url;

    if (!persistedUrl || hasRefreshedBotDataRef.current) return;

    hasRefreshedBotDataRef.current = true;

    (async () => {
      try {
        const response = await axiosGet(
          `/users/completeBot?url=${encodeURIComponent(persistedUrl)}`,
        );
        const freshBotData = response.data?.data;

        if (!freshBotData?.botCustomId) return;

        queryClient.setQueryData(
          ["bot", "completeBot", { botUrl: persistedUrl }],
          freshBotData,
        );

        const normalizedBotData = normalizePersistedBotData(freshBotData);
        storeDispatch({ type: "set-botData", botData: normalizedBotData });
        storeDispatch({
          type: "set-onboarding-botData",
          onboardingBotData: {
            botCustomId: normalizedBotData.botCustomId,
            botdata: normalizedBotData,
            steps: [],
          },
        });
      } catch (error) {
        // Non-fatal: keep the persisted copy so onboarding stays usable.
        console.error("Failed to refresh bot data on load:", error);
      }
    })();
  }, [
    normalizePersistedBotData,
    queryClient,
    store.botData?.url,
    storeDispatch,
  ]);

  const ensurePersistedBotData = useCallback(async () => {
    if (store.botData?.botCustomId && !store.botData?.isDraft) {
      return store.botData;
    }

    if (store.onboardingBotData?.botdata?.botCustomId) {
      return store.onboardingBotData.botdata;
    }

    const projectName =
      values.agentName.trim() || store.botData?.projectName?.trim();
    if (!projectName) {
      throw new Error("Please enter an agent name first.");
    }

    const response = await axiosPost("/users/createBot", {
      projectName,
      botType: "ai",
      enableGemini: companyDetails?.fromGemini ?? false,
    });

    queryClient.invalidateQueries({ queryKey: ["bots"] });
    queryClient.invalidateQueries({ queryKey: ["workSpaces"] });
    queryClient.invalidateQueries({ queryKey: ["planDetails"] });

    const botData = response.data.data;
    let enrichedBotData = botData;
    if (botData?.url) {
      try {
        const completeBotResponse = await axiosGet(
          `/users/completeBot?url=${encodeURIComponent(botData.url)}`,
        );
        const completeBotData = completeBotResponse.data?.data;

        if (completeBotData?.botCustomId) {
          enrichedBotData = completeBotData;
          queryClient.setQueryData(
            ["bot", "completeBot", { botUrl: botData.url }],
            completeBotData,
          );
        }
      } catch (error) {
        console.error("Failed to fetch complete bot after creation:", error);
      }
    }

    const normalizedBotData = normalizePersistedBotData(enrichedBotData);

    if (botData?.projectName && botData?.botCustomId) {
      axiosPost("/audience/lists", {
        name: botData.projectName,
        list_id: botData.botCustomId,
        type: "bot",
      }).catch(() => {});
    }

    storeDispatch({ type: "set-botData", botData: normalizedBotData });
    const state = {
      botCustomId: normalizedBotData.botCustomId,
      botdata: normalizedBotData,
      steps: [],
    };
    storeDispatch({ type: "set-onboarding-botData", onboardingBotData: state });

    // Notify parent (BuilderContainer) that the bot was created so it can update the URL
    if (!botCreatedCalledRef.current && normalizedBotData.url) {
      botCreatedCalledRef.current = true;
      onBotCreated?.(normalizedBotData.url);
    }

    return normalizedBotData;
  }, [
    companyDetails?.fromGemini,
    normalizePersistedBotData,
    onBotCreated,
    queryClient,
    store.botData,
    storeDispatch,
    values.agentName,
  ]);

  const handleFetch = useCallback(
    async (
      url: string,
      mode: "crawl" | "sitemap" | "extra",
      crawledUrls: string[] = [],
      refresh = false,
      botDataOverride?: Record<string, any>,
    ) => {
      const trainedUrls: string[] = [];
      const botData = botDataOverride ?? store.botData;
      const existingUrls = botData?.aiSource?.urls ?? [];
      const botCustomId = botData?.botCustomId;

      if (!botCustomId) throw new Error("Agent data is not ready yet.");

      for (const data of existingUrls) {
        try {
          if (new URL(url).origin === new URL(data.url ?? data.link).origin) {
            trainedUrls.push(data.url);
          }
        } catch {
          // Ignore malformed legacy source URLs while crawling new content.
        }
      }

      const response = await axiosPost("/ai/crawl", {
        url,
        mode,
        crawledUrls,
        trainedUrls,
        botCustomId,
        refresh,
      });

      const {
        crawlData = [],
        fetchRemains,
        stats = {},
      } = response.data?.data ?? {};
      const existingUrlMap = new Map<string, any>(
        existingUrls.map((item) => [item.url, item] as const),
      );
      const result: Map<string, Url> = new Map();

      for (const data of crawlData) {
        const existingUrl = existingUrlMap.get(data.url);
        if (existingUrl && !refresh) continue;

        result.set(nanoid(), {
          ...data,
          ...(existingUrl
            ? {
                status: existingUrl.status,
                characterCount:
                  existingUrl.characterCount ?? data.characterCount ?? 0,
              }
            : null),
          selected: false,
          characterCount:
            existingUrl?.characterCount ?? data.characterCount ?? 0,
        });
      }

      setAllTrained(Boolean(!stats.new && stats.old));
      if (mode !== "extra") setFetchRemains(Boolean(fetchRemains));

      return result;
    },
    [store.botData],
  );

  const handleBrandingFetch = useCallback(
    async (url: string, botDataOverride?: Record<string, any>) => {
      const botData = botDataOverride ?? store.botData;
      const botCustomId = botData?.botCustomId;
      if (!botCustomId) throw new Error("Agent data is not ready yet.");

      try {
        const response = await axiosPost("/ai/fetchBranding", {
          url,
          botCustomId,
        });

        return response.data?.data ?? {};
      } catch (error) {
        // console.error("Branding fetch failed:", error);
        // dispatch(
        //   userSlice.actions.setToastPopUp({
        //     show: true,
        //     toastType: "error",
        //     message: "failed to fetch branding",
        //   }),
        // );
        return {};
      }
    },
    [store.botData],
  );

  const fetchWebsiteContent = useCallback(async () => {
    try {
      if (fetchLoader || fetchMoreLoader || extraLoader) return;

      let websiteUrl: string;
      try {
        websiteUrl = sanitizeUrl(values.websiteUrl.trim());
      } catch {
        setFieldError("Please enter a valid URL (e.g. https://example.com).");
        return;
      }
      setFieldError("");
      const mode = websiteUrl.endsWith(".xml") ? "sitemap" : "crawl";
      const brandingFetchUrl = brandingUrl(websiteUrl);
      const shouldRefreshExistingBot = Boolean(
        store.botData?.botCustomId &&
        !store.botData?.isDraft &&
        store.botData?.aiSource?.urls?.length,
      );
      setOverlayDomain(brandingFetchUrl);
      setFetchLoader(true);
      setFetchedRootUrl(websiteUrl);

      const botData = await ensurePersistedBotData();
      const hasExistingUrls = Boolean(botData?.aiSource?.urls?.length);

      const crawlData = await handleFetch(
        websiteUrl,
        mode,
        [],
        shouldRefreshExistingBot,
        botData,
      );

      if (!hasExistingUrls) {
        const brandingData = await handleBrandingFetch(
          brandingFetchUrl,
          botData,
        );

        const brandingContent = {
          ...(brandingData?.content ?? {}),
          __llm_button_reasoning:
            brandingData?.brandingResult?.__llm_button_reasoning ||
            brandingData?.content?.brandingResult?.__llm_button_reasoning ||
            brandingData?.__llm_button_reasoning,
        };
        const nextChatConfig = {
          ...(await buildChatConfigFromBranding(
            botData?.chatConfig ?? {},
            brandingContent,
          )),
          omniBranding: true,
          customCSS: botData?.chatConfig?.customCSS ?? "",
          customCSSConversation:
            botData?.chatConfig?.customCSSConversation ?? "",
          direction: botData?.chatConfig?.direction ?? "ltr",
          showUserAvatar: botData?.chatConfig?.showUserAvatar ?? true,
          avatarName: botData?.chatConfig?.avatarName ?? "",
          headerName:
            botData?.chatConfig?.headerName ??
            botData?.chatConfig?.greetingConfig?.header?.text ??
            "Hello 👋",
          showHeaderDesc:
            botData?.chatConfig?.showHeaderDesc ??
            botData?.chatConfig?.greetingConfig?.description?.enabled ??
            true,
          headerDescription:
            botData?.chatConfig?.headerDescription ??
            botData?.chatConfig?.greetingConfig?.description?.text ??
            "Enhance your business with ChatBot Builder",
        };

        storeDispatch({
          type: "set-botData",
          botData: {
            ...botData,
            chatConfig: nextChatConfig,
          },
        });
        saveBot({ ...botData, chatConfig: nextChatConfig });
      }

      storeDispatch({ type: "url-set", url: websiteUrl });
      storeDispatch({ type: "extra-delete-all" });
      storeDispatch({ type: "crawl-init", crawlData });
      setFetchModal(true);
    } catch (error: any) {
      showError(
        error.response?.data?.message ??
          error.message ??
          "Unable to fetch website content.",
      );
    } finally {
      setFetchLoader(false);
    }
  }, [
    extraLoader,
    fetchLoader,
    fetchMoreLoader,
    handleFetch,
    handleBrandingFetch,
    ensurePersistedBotData,
    sanitizeUrl,
    showError,
    store.botData?.botCustomId,
    store.botData?.aiSource?.urls?.length,
    store.botData?.isDraft,
    storeDispatch,
    values.websiteUrl,
  ]);

  const fetchMoreUrl = useCallback(async () => {
    try {
      if (fetchLoader || fetchMoreLoader || extraLoader || !fetchedRootUrl) {
        return;
      }

      setFetchMoreLoader(true);
      const crawledUrls: string[] = [];
      store.crawlData.forEach((value) => crawledUrls.push(value.url));

      const crawlData = await handleFetch(fetchedRootUrl, "crawl", crawledUrls);
      storeDispatch({ type: "crawl-add", crawlData });
    } catch (error: any) {
      showError(
        error.response?.data?.message ??
          error.message ??
          "Unable to fetch more links.",
      );
    } finally {
      setFetchMoreLoader(false);
    }
  }, [
    extraLoader,
    fetchLoader,
    fetchMoreLoader,
    fetchedRootUrl,
    handleFetch,
    showError,
    store.crawlData,
    storeDispatch,
  ]);

  const fetchExtraUrl = useCallback(async () => {
    try {
      if (!extraUrl || fetchLoader || fetchMoreLoader || extraLoader) return;

      setExtraLoader(true);
      const url = processUrl(extraUrl);
      const existingUrls = store.botData?.aiSource?.urls ?? [];

      if (existingUrls.some((data: any) => data.url === url)) {
        throw new Error("Link already added");
      }

      let alreadyFetched = false;
      store.crawlData.forEach((value) => {
        if (value.url === url) alreadyFetched = true;
      });

      if (alreadyFetched) throw new Error("Link already fetched");

      const extraData: Map<string, Url> = new Map();
      extraData.set(nanoid(), {
        url,
        characterCount: 0,
        syncInterval: false,
        selected: false,
      });

      storeDispatch({ type: "extra-add", extraData, selected: true });
      setExtraUrl("");
    } catch (error: any) {
      showError(
        error.response?.data?.message ?? error.message ?? "Unable to add link.",
      );
    } finally {
      setExtraLoader(false);
    }
  }, [
    extraLoader,
    extraUrl,
    fetchLoader,
    fetchMoreLoader,
    showError,
    store.botData?.aiSource?.urls,
    store.crawlData,
    storeDispatch,
  ]);

  const characterUsed = store.botData?.aiBotConfig?.characterUsed ?? 0;
  const characterLimit =
    plan?.ai_currentLimits?.characters ??
    store.botData?.aiBotConfig?.characterLimit ??
    Number.MAX_SAFE_INTEGER;
  const shouldShowOnboardingPreview = currentStep.id !== 1;
  const shouldShowDesktopPreview = !isSmallScreen;

  const handleActionClick = async (action: StepAction) => {
    if (action.id !== "back" && isCurrentFieldInvalid) {
      return;
    }

    switch (action.id) {
      case "back":
        if (currentStep.id === 3 && appearancePreviewTab === "conversation") {
          setAppearancePreviewTab("welcome");
          break;
        }
        goToStepByOffset(-1);
        break;
      case "continue":
        if (currentStep.id === 3 && appearancePreviewTab === "welcome") {
          setAppearancePreviewTab("conversation");
          break;
        }
        if (currentStep.id === 1) {
          const trimmedName = values.agentName.trim();
          if (trimmedName.length < 3) {
            setFieldError("Agent name must be at least 3 characters.");
            return;
          }
          if (trimmedName.length > 30) {
            setFieldError("Agent name must be 30 characters or less.");
            return;
          }
          try {
            setBotCreateLoader(true);
            setFieldError("");
            if (store.botData?.botCustomId && !store.botData?.isDraft) {
              const nextBotData: Record<string, any> = {
                ...store.botData,
                projectName: trimmedName,
              };
              storeDispatch({ type: "set-botData", botData: nextBotData });
              storeDispatch({
                type: "set-onboarding-botData",
                onboardingBotData: {
                  ...(store.onboardingBotData ?? {}),
                  botCustomId: nextBotData.botCustomId,
                  botdata: nextBotData,
                },
              });
              await saveBot(nextBotData);
            } else {
              await ensurePersistedBotData();
            }
            goToStepByOffset(1);
          } catch (error: any) {
            showError(
              error.response?.data?.message ??
                error.message ??
                "Unable to save agent name.",
            );
          } finally {
            setBotCreateLoader(false);
          }
          break;
        }
        goToStepByOffset(1);
        break;
      case "customizeAppearance":
        if (!hasTrainingContent) return;
        setAppearancePreviewTab("welcome");
        goToStepByOffset(1);
        break;
      case "finishSetup":
        if (onboardingPageStorageKey) {
          window.sessionStorage.removeItem(onboardingPageStorageKey);
        }
        if (userId) {
          deleteCookies(`onBoarding:${userId}`);
        }
        // Inline (new-bot inside builder): just notify the parent, no overlay needed
        if (onComplete) {
          onComplete();
          break;
        }
        setIsFinishingSetup(true);
        setTimeout(() => {
          if (!inline) {
            if (store.botData?.userId) {
              setCookie(
                `company-onboarding-triggerMilaBot:${store.botData.userId}`,
                "true",
                2,
                environmentData?.PUBLIC_DOMAIN,
                "/",
              );
            }
            dispatch(userSlice.actions.setHelloBarDelay(true));
          }

          if (store.botData?.url) {
            window.history.replaceState(window.history.state, "", "/dashboard");
            navigate(`/builder/${store.botData.url}`, {
              state: { showCompletionModal: true },
            });
          } else {
            navigate("/dashboard");
          }
        }, 1500);
        break;
      default:
        break;
    }
  };

  const handleSkipToBuilder = () => {
    if (onboardingPageStorageKey) {
      window.sessionStorage.removeItem(onboardingPageStorageKey);
    }
    if (userId) {
      deleteCookies(`onBoarding:${userId}`);
    }
    if (onSkip) {
      onSkip();
      return;
    }
    if (onComplete) {
      onComplete();
      return;
    }
    if (store.botData?.url) {
      window.history.replaceState(window.history.state, "", "/dashboard");
      navigate(`/builder/${store.botData.url}`);
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <>
      <motion.div
        className={`onboarding-wrapper flex w-full flex-row justify-between gap-[20px] overflow-hidden bg-white p-[30px] max-[1200px]:p-[20px] ${inline ? "h-full" : "h-[100dvh]"} `}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.42, 0, 0.58, 1] }}
      >
        <OnBoardingStepsWrapper
          activeStep={activeStep}
          currentStep={currentStep}
          currentStepIndex={currentStepIndex}
          completedStepIds={completedStepIds}
          isCurrentFieldInvalid={isCurrentFieldInvalid}
          steps={steps}
          values={values}
          handleActionClick={handleActionClick}
          handleFieldChange={handleFieldChange}
          appearancePreviewTab={appearancePreviewTab}
          fieldError={fieldError}
          inline={inline}
          className={showPreview && isSmallScreen ? "builder_with_preview" : ""}
          onSkip={handleSkipToBuilder}
          loadingActionId={botCreateLoader ? "continue" : null}
          disabledActionIds={
            currentStep.id === 2 && !hasTrainingContent
              ? ["customizeAppearance"]
              : []
          }
          stepComponentProps={{
            restartPolling,
            handleFieldChange,
            fetchWebsiteContent,
            isWebsiteImportLoading: fetchLoader,
            websiteUrlError: fieldError,
          }}
        />
        {(isSmallScreen
          ? shouldShowOnboardingPreview
          : shouldShowDesktopPreview) &&
          (isSmallScreen ? (
            <div
              className={`prev_wrap relative z-[9] m-[10px] flex gap-20 ${
                showPreview ? "w-[32%]" : "w-40"
              } onboarding-prev-wrap`}
            >
              <div
                className="show_preview z-[1]"
                onClick={() => setShowPreview((prev) => !prev)}
              >
                <div className={`arr_icon ${showPreview ? "rotate_arr" : ""}`}>
                  <ArrowIcon />
                </div>

                <div className="content">
                  <ChatPreviewIcon />
                  <p>Chat Preview</p>
                </div>
              </div>

              {showPreview && (
                <div className="ai-bot-preview fx fx-d-col bot-prev-responsive view_prev onboarding-responsive-preview absolute left-[60px] top-0 z-[20] h-full max-[1280px]:left-auto max-[1280px]:right-[60px] max-[1280px]:h-[calc(100dvh-50px)] max-[1200px]:max-h-[unset]">
                  <svg
                    className="absolute"
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={22}
                    viewBox="0 0 20 22"
                    fill="none"
                  >
                    <path
                      d="M2 14.5299C-0.666669 12.9903 -0.666666 9.14133 2 7.60173L14 0.67352C16.6667 -0.86608 20 1.05842 20 4.13762L20 17.994C20 21.0732 16.6667 22.9977 14 21.4581L2 14.5299Z"
                      fill={store?.botData?.chatConfig?.chatBgColor}
                    />
                  </svg>

                  <OnBoardingPreview
                    activeStep={activeStep}
                    appearancePreviewTab={appearancePreviewTab}
                    setAppearancePreviewTab={setAppearancePreviewTab}
                    botData={store.botData}
                    canLoadAgentPreview={hasUnlockedAgentPreview}
                    className="onboarding-preview-wrapper-responsive"
                  />
                </div>
              )}
            </div>
          ) : (
            <OnBoardingPreview
              activeStep={activeStep}
              appearancePreviewTab={appearancePreviewTab}
              setAppearancePreviewTab={setAppearancePreviewTab}
              botData={store.botData}
              canLoadAgentPreview={hasUnlockedAgentPreview}
            />
          ))}
        <OnboardingProgressToast />
      </motion.div>
      {fetchModal && (
        <ModalWrapper>
          <FetchUrlModal
            characterLimit={characterLimit}
            characterUsed={characterUsed}
            fetchMoreUrl={fetchMoreUrl}
            fetchExtraUrl={fetchExtraUrl}
            refreshUrl={fetchWebsiteContent}
            extraUrl={extraUrl}
            setExtraUrl={setExtraUrl}
            refreshLoader={fetchLoader}
            fetchMoreLoader={fetchMoreLoader}
            extraLoader={extraLoader}
            fetchRemains={fetchRemains}
            allTrained={allTrained}
            crawlLimit={plan?.ai_currentLimits?.crawlLimit ?? 3000}
            onClose={() => setFetchModal(false)}
            fetchModal={fetchModal}
            onTrainComplete={() => {
              setFetchModal(false);
            }}
            fromOnboarding={true}
          />
        </ModalWrapper>
      )}
      {fetchLoader && (
        <OnboardingFetchOverlay inline={inline} domain={overlayDomain} />
      )}
      {isFinishingSetup && (
        <OnboardingFetchOverlay
          inline={inline}
          messages={[
            "Setting up your builder...",
            "Polishing your new agent...",
            "Preparing your workspace...",
            "Unlocking advanced tools...",
            "Taking you to the builder to explore...",
            "Almost ready for takeoff...",
          ]}
          subText="We are configuring your workspace and opening the builder."
          domain={overlayDomain}
        />
      )}

      {showSmallScreenModal && <SmallScreenModal />}
      {showLandscapeModeModal && (
        <SmallScreenModal
          iconVariant="rotateDevice"
          heading="Rotate your device"
          subheading={
            <>
              To continue, switch your device to <strong>landscape mode</strong>
              . The agent builder requires a minimum screen width of{" "}
              <strong>1024px</strong>.
            </>
          }
          autoCloseAtWidth={1024}
          onAutoClose={() => setShowLandscapeModeModal(false)}
          cta={null}
        />
      )}
    </>
  );
};

export default OnBoardingWrapper;
